import { Fragment, useState } from 'react';
import { HtmlWithKatex } from './renderKatex';
import { fractionsEnProse } from '../../../utils/fractionsProse';
import { dimensionsSvg } from './svgNaturel';
import PrintNumberLine from './renderNumberLine';
import PrintTable from './renderTable';
import PrintCompasScene, { PrintRapporteurScene } from './renderCompasScene';
import { sansRappelBrouillon } from '../../../utils/rappelBrouillon';

/**
 * Rend l'énoncé d'un exercice en prenant en compte les `elements_additionnels`.
 *
 * Comportement :
 *   1. Parse `texte` à la recherche de `{{element:ID}}` (regex
 *      /\{\{element:(\d+)\}\}/g). ID = index dans `elements[]`.
 *   2. Pour chaque segment texte → rendu via `HtmlWithKatex` (conserve le
 *      support `$...$` inline existant).
 *   3. Pour chaque placeholder → remplace par le rendu de l'élément, inline
 *      si expression, bloc sinon (image, numberline).
 *   4. Les éléments **non référencés** dans le texte sont rendus en blocs
 *      après l'énoncé (préserve l'intention prof).
 *   5. La table V/F (`type === 'table'` + `config.type === 'vraiFaux'`) est
 *      **ignorée** dans l'énoncé — gérée séparément par `PrintVraiFaux`.
 *
 * Gestion d'erreur : si un placeholder pointe un ID inexistant, affiche un
 * badge rouge pâle au lieu de planter (aide debug côté prof).
 */

const PLACEHOLDER_RE = /\{\{element:(\d+)\}\}/g;
/* Rappel « ✏️ Pense à poser tes calculs sur ton brouillon » : retiré sur
   papier, l'élève a sa feuille (2026-09-03). Règle partagée avec la
   projection : utils/rappelBrouillon.js. */

const isInlineElement = (el) => el?.type === 'expression';

/**
 * Encadré visible quand un média ne peut pas s'imprimer (image en erreur,
 * geogebra sans capture). JAMAIS de skip silencieux : le prof doit le voir
 * dans l'aperçu AVANT d'imprimer. Hauteur fixe (CSS) → la mesure de
 * pagination reste stable même quand le média est mort.
 */
export const PrintMediaPlaceholder = ({ message, detail }) => (
    <div className="ep-media-placeholder">
        <span className="ep-media-placeholder-titre">⚠ {message}</span>
        {detail ? <span className="ep-media-placeholder-detail">{detail}</span> : null}
    </div>
);

/**
 * Image d'impression : rend un <img> et bascule sur un placeholder visible
 * de hauteur fixe en cas d'erreur de chargement (URL expirée, réseau) — la
 * pagination mesurée reste juste, et le prof est prévenu au lieu d'imprimer
 * un trou.
 */
export const PrintImg = ({ src, alt = '', className = '' }) => {
    const [enErreur, setEnErreur] = useState(false);
    if (!src || enErreur) {
        return <PrintMediaPlaceholder message="Image non chargée" detail={alt || undefined} />;
    }
    // SVG des générateurs : la largeur naturelle est un PLAFOND (jamais
    // agrandi sur papier, cf. svgNaturel.js et .ep-enonce-img). Les images
    // sans dimension connue (PNG) n'ont pas de variable → CSS historique.
    const dims = dimensionsSvg(src);
    return (
        <img
            className={className}
            src={src}
            alt={alt}
            style={dims ? { '--ep-svg-w': `${dims.w}px` } : undefined}
            onError={() => setEnErreur(true)}
        />
    );
};

/**
 * `skipTables` : activé par PrintVraiFaux/PrintVraiFauxGroup qui rendent
 * eux-mêmes le tableau de réponse (vraiFaux OU colonnesLibres) — évite le
 * double rendu dans l'énoncé. Hors de ce contexte, seules les tables vraiFaux
 * sont exclues : une table générique peut être purement informative (données
 * d'un exercice texte/association) et doit rester dans l'énoncé.
 */
const shouldSkipElement = (el, skipTables) =>
    el?.type === 'table' && (skipTables || el?.config?.type === 'vraiFaux');

/** Rendu d'un élément selon son type. `inline` = rendu en span (expressions). */
const renderElement = (el, key, { inline = false } = {}) => {
    if (!el) return null;
    const { type, config = {} } = el;

    if (type === 'image') {
        const src = config.imageUrl
            || (config.imageData
                ? (config.imageData.startsWith('data:')
                    ? config.imageData
                    : `data:image/png;base64,${config.imageData}`)
                : null);
        const sizeClass = config.maxWidth
            ? ` ep-img-${config.maxWidth}`
            : '';
        return (
            <PrintImg
                key={key}
                className={`ep-enonce-img${sizeClass}`}
                src={src}
                alt={config.alt || ''}
            />
        );
    }

    if (type === 'expression') {
        // config.contenu est une string LaTeX. Peut contenir `$...$` déjà,
        // ou être du LaTeX brut. On force l'entourage `$…$` si absent pour
        // que HtmlWithKatex détecte et render.
        const raw = config.contenu || '';
        const html = raw.includes('$') ? raw : `$${raw}$`;
        if (inline) {
            return <HtmlWithKatex key={key} as="span" html={html} />;
        }
        return (
            <HtmlWithKatex
                key={key}
                as="div"
                className="ep-enonce-expression"
                html={html}
            />
        );
    }

    if (type === 'numberline') {
        return (
            <PrintNumberLine key={key} {...(config || {})} />
        );
    }

    // Tables génériques (colonnesLibres…) — les vraiFaux sont exclues en amont
    // (shouldSkipElement) et gérées par PrintVraiFaux. Cellules vides : l'élève
    // complète au stylo.
    if (type === 'table') {
        return <PrintTable key={key} colonnes={config.colonnes} lignes={config.lignes} />;
    }

    // Outils interactifs de géométrie : on n'imprime que la scène de base
    // (figures, points, demi-droites) — l'élève manipule son vrai compas /
    // rapporteur sur la feuille.
    if (type === 'compas_interactif') {
        return <PrintCompasScene key={key} {...(config || {})} />;
    }
    if (type === 'rapporteur_interactif') {
        return <PrintRapporteurScene key={key} {...(config || {})} />;
    }

    if (type === 'geogebra') {
        // Fallback : si `ggb_image` base64 présent, l'afficher comme image
        const img = config.ggb_image || config.ggb_svg;
        if (img) {
            const src = img.startsWith('data:') || img.startsWith('<svg')
                ? (img.startsWith('<svg') ? `data:image/svg+xml;utf8,${encodeURIComponent(img)}` : img)
                : `data:image/png;base64,${img}`;
            return <PrintImg key={key} className="ep-enonce-img" src={src} alt="Figure GeoGebra" />;
        }
        // Pas de capture disponible → encadré VISIBLE dans l'aperçu ET sur
        // papier (fini le skip silencieux qui laissait un trou dans l'énoncé).
        return (
            <PrintMediaPlaceholder
                key={key}
                message="Figure indisponible à l'impression"
                detail="Figure GeoGebra sans capture imprimable."
            />
        );
    }

    // Type inconnu / non supporté
    // eslint-disable-next-line no-console
    console.warn(`[PrintEnonce] Type d'élément inconnu : ${type}`);
    return null;
};

const MissingBadge = ({ id }) => (
    <span className="ep-element-missing">{`{{element:${id}}}`}</span>
);

/**
 * `skipIndices` : indices d'éléments à NE PAS rendre (figure partagée hissée
 * en tête d'un bloc groupé). Les indices du tableau `elements` restent
 * stables — on ne filtre jamais la liste, les placeholders `{{element:N}}`
 * étant positionnels.
 */
const PrintEnonce = ({ texte: texteBrut, elements, className = 'ep-exo-enonce', skipTables = false, skipIndices = null }) => {
    // Rappel écran retiré, puis fraction seule en style texte dans la prose,
    // calcul intact (utils/fractionsProse.js, 2026-09-06).
    const texte = typeof texteBrut === 'string' ? fractionsEnProse(sansRappelBrouillon(texteBrut)) : texteBrut;
    const els = Array.isArray(elements) ? elements : [];
    const skipped = Array.isArray(skipIndices) ? new Set(skipIndices) : null;
    const referenced = new Set();

    // 1. Parse le texte en segments (texte + placeholders)
    const segments = [];
    if (typeof texte === 'string' && texte.length > 0) {
        let lastIndex = 0;
        let m;
        PLACEHOLDER_RE.lastIndex = 0;
        while ((m = PLACEHOLDER_RE.exec(texte)) !== null) {
            if (m.index > lastIndex) {
                segments.push({ kind: 'text', content: texte.substring(lastIndex, m.index) });
            }
            const id = parseInt(m[1], 10);
            segments.push({ kind: 'slot', id });
            referenced.add(id);
            lastIndex = m.index + m[0].length;
        }
        if (lastIndex < texte.length) {
            segments.push({ kind: 'text', content: texte.substring(lastIndex) });
        }
    }

    // 2. Rendu des segments
    const rendered = segments.map((seg, i) => {
        if (seg.kind === 'text') {
            return <HtmlWithKatex key={`t-${i}`} as="span" html={seg.content} />;
        }
        const el = els[seg.id];
        if (!el) return <MissingBadge key={`m-${i}`} id={seg.id} />;
        if (skipped?.has(seg.id)) return null;
        if (shouldSkipElement(el, skipTables)) return null;
        return renderElement(el, `e-${i}`, { inline: isInlineElement(el) });
    });

    // 3. Éléments orphelins (non référencés) → rendus en blocs après
    const orphans = els
        .map((el, idx) => ({ el, idx }))
        .filter(({ el, idx }) =>
            el && !referenced.has(idx) && !skipped?.has(idx) && !shouldSkipElement(el, skipTables)
        );

    if (segments.length === 0 && orphans.length === 0) return null;

    return (
        <div className={className}>
            {rendered.length > 0 && <Fragment>{rendered}</Fragment>}
            {orphans.map(({ el, idx }) => (
                <Fragment key={`orphan-${idx}`}>
                    {renderElement(el, `orphan-${idx}-el`, { inline: false })}
                </Fragment>
            ))}
        </div>
    );
};

export default PrintEnonce;
